/**
 * IMMUTABLE AUTH SYSTEM - MAIN EXPORT
 * 
 * This is the ONLY entry point for the auth system.
 * All auth functionality MUST go through this interface.
 * 
 * CRITICAL RULES:
 * 1. NO direct imports from individual auth files
 * 2. NO modifications to this interface
 * 3. NO bypassing of auth protection
 * 4. ALL auth operations MUST use these exports
 */

// Core auth system
export { AuthCore } from './AuthCore';
export type { AuthState } from './AuthCore';

// React hooks
export { useAuthCore, useAuthGuard } from './useAuthCore';

// Auth guards and protection - moved to components/auth/AuthGuard
// export { AuthGuard, LoginRequired, ProtectedPage } from './AuthGuard';

// Backend service
export { backendAuthService } from './BackendAuthService';
export type { BackendResponse, RequestConfig } from './BackendAuthService';

// Error boundary
export { AuthErrorBoundary } from './AuthErrorBoundary';

// Circuit breaker (read-only access)
export { AuthProtection } from './AuthCircuitBreaker';

// Monitoring (read-only access)
export { authMonitoring } from './AuthMonitoring';
export type { AuthMonitoringEvent, AuthHealthMetrics, SecurityAlert } from './AuthMonitoring';

// Import instances for internal use
import { AuthCore } from './AuthCore';
import { backendAuthService } from './BackendAuthService';
import { AuthProtection } from './AuthCircuitBreaker';
import { authMonitoring } from './AuthMonitoring';

/**
 * IMMUTABLE AUTH SYSTEM STATUS
 * 
 * This function provides a complete overview of the auth system status.
 * Use this for debugging and monitoring.
 */
export function getAuthSystemStatus() {
  const authState = AuthCore.getInstance().getState();
  const circuitStatus = AuthProtection.getStatus();
  const healthMetrics = authMonitoring.getHealthMetrics();
  const recentAlerts = authMonitoring.getRecentAlerts(5);
  
  return Object.freeze({
    // Auth state
    isAuthenticated: authState.isAuthenticated,
    isLoading: authState.isLoading,
    user: authState.user,
    
    // Circuit breaker status
    circuitState: circuitStatus.state,
    circuitFailures: circuitStatus.failureCount,
    circuitLastFailure: circuitStatus.lastFailureTime,
    
    // Health metrics
    totalLogins: healthMetrics.totalLogins,
    successRate: healthMetrics.successRate,
    isHealthy: authMonitoring.isHealthy(),
    
    // Security alerts
    recentAlerts: recentAlerts,
    hasSecurityIssues: recentAlerts.some((alert: any) => 
      alert.level === 'HIGH' || alert.level === 'CRITICAL'
    ),
    
    // System integrity
    systemIntegrity: {
      coreSealed: Object.isSealed(AuthCore.getInstance()),
      protectionActive: circuitStatus.state === 'CLOSED' || circuitStatus.state === 'HALF_OPEN',
      monitoringActive: authMonitoring.isHealthy()
    }
  });
}

/**
 * EMERGENCY AUTH SYSTEM DIAGNOSTICS
 * 
 * Use this function only for emergency debugging.
 * It provides detailed system information.
 */
export function getAuthSystemDiagnostics() {
  const status = getAuthSystemStatus();
  const recentEvents = authMonitoring.getRecentEvents(20);
  
  return Object.freeze({
    ...status,
    
    // Detailed diagnostics
    diagnostics: {
      timestamp: new Date().toISOString(),
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'Unknown',
      url: typeof window !== 'undefined' ? window.location.href : 'Unknown',
      
      // Recent events
      recentEvents: recentEvents,
      
      // System checks
      systemChecks: {
        localStorageAvailable: typeof localStorage !== 'undefined',
        supabaseClientExists: true, // Supabase client is always available in AuthCore
        jwtSecretConfigured: !!import.meta.env.VITE_JWT_SECRET,
        backendUrlConfigured: !!import.meta.env.VITE_API_BASE_URL
      }
    }
  });
}

/**
 * AUTH SYSTEM HEALTH CHECK
 * 
 * Quick health check function for monitoring.
 */
export function isAuthSystemHealthy(): boolean {
  try {
    const status = getAuthSystemStatus();
    
    return (
      status.systemIntegrity.coreSealed &&
      status.systemIntegrity.protectionActive &&
      status.systemIntegrity.monitoringActive &&
      !status.hasSecurityIssues &&
      status.circuitState === 'CLOSED'
    );
  } catch (error) {
    console.error('Auth system health check failed:', error);
    return false;
  }
}

// Freeze all exports to prevent tampering (except AuthCore which needs mutable static instance)
// Object.freeze(AuthCore); // Commented out - AuthCore needs mutable static instance property
Object.freeze(backendAuthService);
Object.freeze(authMonitoring);
Object.freeze(AuthProtection);

// System initialized

// Prevent any modifications to this module
if (typeof window !== 'undefined') {
  (window as any).__AUTH_SYSTEM_SEALED__ = true;
}